// src/pages/Notifications.js
import React, { useState, useEffect } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { notificationsAdminService } from '../services/notificationsAdminService';
import { employeeService } from '../services/employeeService';
import { companyService } from '../services/companyService';
import './Notifications.css';

const NOTIFICATION_TYPES = [
  { id: 'offer', name: ' Nouvelle offre', icon: '🎁', color: '#4f46e5' },
  { id: 'survey', name: ' Nouvelle enquête', icon: '📊', color: '#10b981' },
  { id: 'event', name: ' Événement prochain', icon: '📅', color: '#f59e0b' },
  { id: 'company', name: ' Info entreprise', icon: '🏢', color: '#ef4444' },
  { id: 'system', name: ' Système', icon: '🔔', color: '#6b7280' },
];

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedNotification, setSelectedNotification] = useState(null);

  const [newNotification, setNewNotification] = useState({
    title: '',
    message: '',
    type: 'offer',
    companyId: '',
    targetType: 'all',
    targetEmployees: [],
    expiresAt: '',
  });

  const [stats, setStats] = useState({
    total: 0,
    unread: 0,
    byType: {},
    byCompany: {}
  });

  // ==================== CHARGEMENT ====================
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const data = await notificationsAdminService.getNotifications();
      
      const groupMap = new Map();
      
      data.forEach(notif => {
        const key = `${notif.title}|${notif.message}|${notif.type}|${notif.companyId || ''}`;
        
        if (!groupMap.has(key)) {
          groupMap.set(key, {
            id: key,
            title: notif.title,
            message: notif.message,
            type: notif.type,
            companyId: notif.companyId,
            createdAt: notif.createdAt,
            isActive: notif.isActive !== undefined ? notif.isActive : true,
            sentCount: 0,
            readCount: 0,
            allIds: []
          });
        }
        
        const group = groupMap.get(key);
        group.sentCount++;
        if (notif.isRead) group.readCount++;
        group.allIds.push(notif._id);
      });
      
      setNotifications(Array.from(groupMap.values()));
      
      const statsData = {
        total: groupMap.size,
        unread: 0,
        byType: {},
        byCompany: {}
      };
      
      NOTIFICATION_TYPES.forEach(type => {
        statsData.byType[type.id] = 0;
      });
      
      groupMap.forEach(g => {
        if (g.readCount < g.sentCount) statsData.unread++;
        statsData.byType[g.type] = (statsData.byType[g.type] || 0) + 1;
        if (g.companyId) {
          statsData.byCompany[g.companyId] = (statsData.byCompany[g.companyId] || 0) + 1;
        }
      });
      
      setStats(statsData);
    } catch (err) {
      setError(err.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const data = await employeeService.getEmployees();
      setEmployees(data.employees || []);
    } catch (err) {
      console.error('Erreur chargement employés:', err);
    }
  };

  const fetchCompanies = async () => {
    try {
      const data = await companyService.getCompanies();
      let companiesData = [];
      if (Array.isArray(data)) companiesData = data;
      else if (data?.data && Array.isArray(data.data)) companiesData = data.data;
      else if (data?.companies && Array.isArray(data.companies)) companiesData = data.companies;
      setCompanies(companiesData);
    } catch (err) {
      console.error('Erreur chargement entreprises:', err);
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchEmployees();
    fetchCompanies();
  }, []);


  const handleCreateNotification = async (e) => {
  e.preventDefault();
  
  if (!newNotification.title || !newNotification.message) {
    setError('Veuillez remplir tous les champs');
    return;
  }

  let targetUserIds = [];
  
  // Filtrer les employés par entreprise
  let baseEmployees = [];
  if (newNotification.companyId === 'all') {
    baseEmployees = employees;
  } else {
    baseEmployees = employees.filter(emp => emp.companyId === newNotification.companyId);
  }
  
  // Filtrer par type de cible
  if (newNotification.targetType === 'all') {
    targetUserIds = baseEmployees.map(emp => emp._id);
  } else if (newNotification.targetType === 'managers') {
    targetUserIds = baseEmployees.filter(emp => 
      emp.role === 'manager' || emp.admin === true
    ).map(emp => emp._id);
  } else if (newNotification.targetType === 'employees') {
    targetUserIds = baseEmployees.filter(emp => 
      emp.role === 'employee' && emp.admin !== true
    ).map(emp => emp._id);
  } else if (newNotification.targetType === 'specific') {
    targetUserIds = newNotification.targetEmployees;
  }

  if (targetUserIds.length === 0) {
    setError('Aucun employé ciblé');
    return;
  }

  try {
    setError('');
    
    await notificationsAdminService.createNotification({
      title: newNotification.title,
      message: newNotification.message,
      type: newNotification.type,
      companyId: newNotification.companyId === 'all' ? null : newNotification.companyId,
      userIds: targetUserIds,
      expiresAt: newNotification.expiresAt || null,
    });
    
    setShowCreateForm(false);
    setNewNotification({
      title: '',
      message: '',
      type: 'offer',
      companyId: '',
      targetType: 'all',
      targetEmployees: [],
      expiresAt: '',
    });
    setSelectedEmployees([]);
    await fetchNotifications();
    alert('✅ Notification envoyée avec succès !');
  } catch (err) {
    setError(err.message || 'Erreur lors de la création');
  }
};

  
  // ==================== ACTIVER/DÉSACTIVER ====================
  const handleToggleActive = async (notification) => {
    const newStatus = !notification.isActive;
    const action = newStatus ? 'activée' : 'désactivée';
    
    if (!window.confirm(`Êtes-vous sûr de vouloir ${action} "${notification.title}" pour tous les employés ?`)) return;
    
    try {
      await notificationsAdminService.toggleBulkActive({
        title: notification.title,
        message: notification.message,
        type: notification.type,
        companyId: notification.companyId,
        isActive: newStatus
      });
      await fetchNotifications();
      alert(`✅ Notification ${action} avec succès`);
    } catch (err) {
      setError(err.message || 'Erreur lors du changement de statut');
    }
  };

  // ==================== SUPPRESSION GROUPÉE ====================
  const handleDeleteNotification = async (notification) => {
    if (!window.confirm(`Supprimer définitivement "${notification.title}" pour les ${notification.sentCount} employés ?`)) return;
    try {
      await notificationsAdminService.deleteBulkNotifications({
        title: notification.title,
        message: notification.message,
        type: notification.type,
        companyId: notification.companyId
      });
      await fetchNotifications();
      alert('✅ Notification supprimée pour tous les employés');
    } catch (err) {
      setError(err.message || 'Erreur lors de la suppression');
    }
  };





  // ==================== UTILITAIRES ====================
  const getTypeInfo = (type) => {
    return NOTIFICATION_TYPES.find(t => t.id === type) || NOTIFICATION_TYPES[0];
  };

  const getCompanyName = (companyId) => {
    if (!companyId) return '🌍 Toutes les entreprises';
    const company = companies.find(c => c._id === companyId);
    return company?.name || companyId || 'Toutes';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredNotifications = notifications.filter(notification => {
    if (typeFilter !== 'all' && notification.type !== typeFilter) return false;
    if (companyFilter !== 'all' && notification.companyId !== companyFilter) return false;
    if (statusFilter !== 'all') {
      if (statusFilter === 'active' && !notification.isActive) return false;
      if (statusFilter === 'inactive' && notification.isActive) return false;
    }
    return true;
  });

  const filteredEmployees = employees.filter(emp => 
    emp.companyId === newNotification.companyId &&
    (emp.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     emp.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     emp.email?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleToggleEmployee = (employeeId) => {
    setSelectedEmployees(prev => 
      prev.includes(employeeId) ? prev.filter(id => id !== employeeId) : [...prev, employeeId]
    );
    setNewNotification(prev => ({
      ...prev,
      targetEmployees: selectedEmployees.includes(employeeId)
        ? prev.targetEmployees.filter(id => id !== employeeId)
        : [...prev.targetEmployees, employeeId]
    }));
  };

  if (loading) {
    return (
      <div className="notifications">
        <div className="page-header">
          <h1>Gestion des Notifications</h1>
          <p>Chargement...</p>
        </div>
        <div className="loading-state">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="notifications">
      <div className="page-header">
        <div className="page-title">
          <h1>🔔 Gestion des Notifications</h1>
          <p>Envoyez et gérez les notifications ({notifications.length} notification{notifications.length > 1 ? 's' : ''})</p>
        </div>
        <Button variant="primary" size="lg" onClick={() => setShowCreateForm(true)}>
          ➕ Nouvelle notification
        </Button>
      </div>

      {/* Statistiques */}
      <div className="notifications-stats">
        <Card className="stat-card" padding="lg">
          <div className="stat-content">
            <div className="stat-icon">📊</div>
            <div className="stat-info">
              <div className="stat-number">{stats.total}</div>
              <div className="stat-label">Total</div>
            </div>
          </div>
        </Card>
        <Card className="stat-card" padding="lg">
          <div className="stat-content">
            <div className="stat-icon">🔔</div>
            <div className="stat-info">
              <div className="stat-number">{stats.unread}</div>
              <div className="stat-label">Non lues</div>
            </div>
          </div>
        </Card>
        {NOTIFICATION_TYPES.map(type => (
          <Card key={type.id} className="stat-card" padding="lg">
            <div className="stat-content">
              <div className="stat-icon">{type.icon}</div>
              <div className="stat-info">
                <div className="stat-number">{stats.byType[type.id] || 0}</div>
                <div className="stat-label">{type.name}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Filtres */}
      <Card className="filters-card" padding="md">
        <div className="filters-container">
          <div className="filter-group">
            <label>Type:</label>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="filter-select">
              <option value="all">📋 Tous les types</option>
              {NOTIFICATION_TYPES.map(type => (
                <option key={type.id} value={type.id}>{type.icon} {type.name}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Entreprise:</label>
            <select value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)} className="filter-select">
              <option value="all">🏢 Toutes les entreprises</option>
              <option value="">🌍 Toutes les entreprises (sans restriction)</option>
              {companies.map(company => (
                <option key={company._id} value={company._id}>🏢 {company.name}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Statut:</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="filter-select">
              <option value="all">📋 Tous</option>
              <option value="active">🟢 Actives</option>
              <option value="inactive">🔴 Inactives</option>
            </select>
          </div>
          {(typeFilter !== 'all' || companyFilter !== 'all' || statusFilter !== 'all') && (
            <Button variant="secondary" size="sm" onClick={() => { setTypeFilter('all'); setCompanyFilter('all'); setStatusFilter('all'); }}>
              🔄 Réinitialiser
            </Button>
          )}
        </div>
      </Card>

      {error && (
        <Card className="error-card" padding="md">
          <div className="error-message">❌ {error}<button onClick={() => setError('')}>✕</button></div>
        </Card>
      )}

      {/* Tableau */}
      <div className="notifications-table-container">
        <table className="notifications-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Titre</th>
              <th>Message</th>
              <th>Entreprise</th>
              <th>Statut</th>
              <th>📤 Envoyé</th>
              <th>✅ Lu</th>
              <th>📊 Taux</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredNotifications.map(notification => {
              const typeInfo = getTypeInfo(notification.type);
              const readRate = notification.sentCount > 0 
                ? Math.round((notification.readCount / notification.sentCount) * 100) 
                : 0;
              
              return (
                <tr key={notification.id}>
                  <td data-label="Type">
                    <span className="type-badge" style={{ backgroundColor: typeInfo.color + '15', color: typeInfo.color }}>
                      {typeInfo.icon} {typeInfo.name}
                    </span>
                  </td>
                  <td data-label="Titre"><strong>{notification.title}</strong></td>
                  <td data-label="Message">
                    <div className="message-preview">
                      {notification.message.length > 60 ? notification.message.substring(0, 60) + '...' : notification.message}
                    </div>
                  </td>
                  <td data-label="Entreprise">🏢 {getCompanyName(notification.companyId)}</td>
                  <td data-label="Statut">
                    <span className={`status-badge ${notification.isActive ? 'status-active' : 'status-inactive'}`}>
                      {notification.isActive ? '🟢 Active' : '🔴 Inactive'}
                    </span>
                  </td>
                  <td data-label="Envoyé" className="stat-number-cell">{notification.sentCount}</td>
                  <td data-label="Lu" className="stat-number-cell">{notification.readCount}</td>
                  <td data-label="Taux">
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${readRate}%`, backgroundColor: readRate > 70 ? '#10b981' : readRate > 30 ? '#f59e0b' : '#ef4444' }}></div>
                      <span className="progress-text">{readRate}%</span>
                    </div>
                  </td>
                  <td data-label="Date">{formatDate(notification.createdAt)}</td>
                  <td data-label="Actions">
                    <div className="table-actions">
                      <button 
                        className={`action-btn ${notification.isActive ? 'deactivate-btn' : 'activate-btn'}`} 
                        onClick={() => handleToggleActive(notification)} 
                        title={notification.isActive ? 'Désactiver' : 'Activer'}
                      >
                        {notification.isActive ? '🔴' : '🟢'}
                      </button>
                      <button className="action-btn view-btn" onClick={() => setSelectedNotification(notification)} title="Voir">👁️</button>
                      <button className="action-btn delete-btn" onClick={() => handleDeleteNotification(notification)} title="Supprimer">🗑️</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredNotifications.length === 0 && (
        <Card className="empty-state">
          <div className="empty-icon">🔔</div>
          <h3>Aucune notification</h3>
          <Button variant="primary" onClick={() => setShowCreateForm(true)}>➕ Créer une notification</Button>
        </Card>
      )}

      {/* Modal création */}
      {showCreateForm && (
        <div className="modal-overlay" onClick={() => setShowCreateForm(false)}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>➕ Nouvelle notification</h2>
              <button className="modal-close" onClick={() => setShowCreateForm(false)}>✕</button>
            </div>
            
            <form onSubmit={handleCreateNotification}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Type *</label>
                    <select value={newNotification.type} onChange={(e) => setNewNotification({...newNotification, type: e.target.value})} required>
                      {NOTIFICATION_TYPES.map(type => (
                        <option key={type.id} value={type.id}>{type.icon} {type.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Date d'expiration</label>
                    <input type="date" value={newNotification.expiresAt} onChange={(e) => setNewNotification({...newNotification, expiresAt: e.target.value})} />
                  </div>
                </div>

                <div className="form-group">
                  <label>Titre *</label>
                  <input type="text" value={newNotification.title} onChange={(e) => setNewNotification({...newNotification, title: e.target.value})} required />
                </div>

                <div className="form-group">
                  <label>Message *</label>
                  <textarea value={newNotification.message} onChange={(e) => setNewNotification({...newNotification, message: e.target.value})} rows="3" required />
                </div>

                <div className="form-section">
                  <h3>🎯 Ciblage</h3>
                  
                  <div className="form-group">
                    <label>Entreprise *</label>
                    <select 
                      value={newNotification.companyId} 
                      onChange={(e) => {
                        setNewNotification({...newNotification, companyId: e.target.value, targetEmployees: []});
                        setSelectedEmployees([]);
                      }} 
                      required
                    >
                      <option value="">Sélectionner une entreprise...</option>
                      <option value="all">🌍 Toutes les entreprises</option>
                      {companies.map(company => (
                        <option key={company._id} value={company._id}>🏢 {company.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Destinataires *</label>
                    <div className="target-options">
                      <label className="target-option">
                        <input type="radio" name="targetType" value="all" checked={newNotification.targetType === 'all'} onChange={(e) => setNewNotification({...newNotification, targetType: e.target.value, targetEmployees: []})} />
                        <span>👥 Tous les employés</span>
                      </label>
                      <label className="target-option">
                        <input type="radio" name="targetType" value="managers" checked={newNotification.targetType === 'managers'} onChange={(e) => setNewNotification({...newNotification, targetType: e.target.value, targetEmployees: []})} />
                        <span>⭐ Managers uniquement</span>
                      </label>
                      <label className="target-option">
                        <input type="radio" name="targetType" value="employees" checked={newNotification.targetType === 'employees'} onChange={(e) => setNewNotification({...newNotification, targetType: e.target.value, targetEmployees: []})} />
                        <span>👤 Employés uniquement</span>
                      </label>
                      <label className="target-option">
                        <input type="radio" name="targetType" value="specific" checked={newNotification.targetType === 'specific'} onChange={(e) => setNewNotification({...newNotification, targetType: e.target.value, targetEmployees: []})} />
                        <span>🎯 Employés spécifiques</span>
                      </label>
                    </div>
                  </div>

                  {newNotification.targetType === 'specific' && (
                    <div className="form-group">
                      <label>Sélectionner les employés</label>
                      <input type="text" placeholder="🔍 Rechercher..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-input-small" />
                      <div className="employees-list">
                        {filteredEmployees.map(emp => (
                          <label key={emp._id} className="employee-checkbox">
                            <input type="checkbox" checked={selectedEmployees.includes(emp._id)} onChange={() => handleToggleEmployee(emp._id)} />
                            <span>{emp.firstName} {emp.lastName}</span>
                            <small>({emp.email})</small>
                            <span className="role-badge-small">{emp.role === 'manager' ? '⭐ Manager' : '👤 Employé'}</span>
                          </label>
                        ))}
                        {filteredEmployees.length === 0 && (
                          <p className="no-employees">Aucun employé dans cette entreprise</p>
                        )}
                      </div>
                      {selectedEmployees.length > 0 && (
                        <div className="selected-count">📌 {selectedEmployees.length} employé(s) sélectionné(s)</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <Button type="button" variant="secondary" onClick={() => setShowCreateForm(false)}>Annuler</Button>
                <Button type="submit" variant="primary">📤 Envoyer la notification</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Détails */}
      {selectedNotification && (
        <div className="modal-overlay" onClick={() => setSelectedNotification(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📋 Détails</h2>
              <button className="modal-close" onClick={() => setSelectedNotification(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="detail-row"><label>Type:</label><span>{getTypeInfo(selectedNotification.type)?.icon} {getTypeInfo(selectedNotification.type)?.name}</span></div>
              <div className="detail-row"><label>Titre:</label><span><strong>{selectedNotification.title}</strong></span></div>
              <div className="detail-row"><label>Message:</label><div className="detail-message">{selectedNotification.message}</div></div>
              <div className="detail-row"><label>Entreprise:</label><span>🏢 {getCompanyName(selectedNotification.companyId)}</span></div>
              <div className="detail-row"><label>Statut:</label><span>{selectedNotification.isActive ? '🟢 Active' : '🔴 Inactive'}</span></div>
              <div className="detail-row"><label>Envoyé à:</label><span>{selectedNotification.sentCount} employé(s)</span></div>
              <div className="detail-row"><label>Lu par:</label><span>{selectedNotification.readCount} employé(s)</span></div>
              <div className="detail-row"><label>Taux de lecture:</label><span>{selectedNotification.sentCount > 0 ? Math.round((selectedNotification.readCount / selectedNotification.sentCount) * 100) : 0}%</span></div>
              <div className="detail-row"><label>Créée le:</label><span>{formatDate(selectedNotification.createdAt)}</span></div>
              {selectedNotification.expiresAt && <div className="detail-row"><label>Expire le:</label><span>{formatDate(selectedNotification.expiresAt)}</span></div>}
            </div>
            <div className="modal-footer">
              <Button variant="danger" onClick={() => handleDeleteNotification(selectedNotification)}>🗑️ Supprimer</Button>
              <Button variant="primary" onClick={() => setSelectedNotification(null)}>Fermer</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;